import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { createOvertimeTableColumns } from '../overtime-table-columns/overtime-table-columns';
import { PayoffDialog } from '../payoff-dialog/payoff-dialog';
import { DEFAULT_OVERTIME_ORDER } from '../../graphql/queries';
import { useGetOvertimeList } from '../../hooks/use-overtime';
import { OvertimePagingResult, OvertimeRecord } from '../../types/overtime.types';

type OvertimeListProps = {
  /** The page a row opens: the employee's overtime. */
  employeePath: (userId: string) => string;
};

/**
 * Every employee's overtime balance, with Pay off (the legacy Overtime Manager). It is the Overtime tab
 * of Team Attendance; a row opens the employee's attendance page on its Overtime tab.
 */
export const OvertimeList = ({ employeePath }: OvertimeListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_OVERTIME_ORDER,
  });
  const [payoffTarget, setPayoffTarget] = useState<OvertimeRecord | null>(null);
  const canPayoff = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.OVERTIME_PAYOFF);

  const { data, isLoading, error } = useGetOvertimeList({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { Overtimes?: OvertimePagingResult })?.Overtimes;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const columns = createOvertimeTableColumns({ t, onPayoff: setPayoffTarget, canPayoff });

  return (
    <>
      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        onRowClick={(row: OvertimeRecord) => navigate(employeePath(row.UserId))}
        mobileView="cards"
      />

      <PayoffDialog
        open={Boolean(payoffTarget)}
        onOpenChange={(open) => !open && setPayoffTarget(null)}
        overtime={payoffTarget}
      />
    </>
  );
};
